import { WidgetType } from "./common.types";
import { Field } from "./field.types";

export const MAX_NESTING_LEVEL = 2;

export interface Canvas {
    containers: Container[];
    activeContainerId?: string;
}

export interface ContainerMetadata {
    type: 'container' | 'nested-container';
    isNested?: boolean;
    parentContainerId?: string;
    rowIndex?: number;
    nestingLevel?: number;
}

export interface ContainerNew {
    id: string;
    name: string;
    order: number;
    configuration: ContainerConfiguration;
    content: ContainerContentNew;
    usageInfo: {
        usedComponents: UsedComponent[];
    };
}

export interface ContainerContentNew {
    submodel?: {
        id: string;
        name: string;
        isTemporary?: boolean;
        configuration: any;
    };
    items: ContainerItem[];
}

export interface Container {
    id: string;
    name: string;
    order: number;
    configuration: ContainerConfiguration;
    metadata?: ContainerMetadata;
    content: {
        type: 'widget' | 'subModel' | 'container';
        componentId: string;
        'widget-type'?: string;
        nestedContainers?: Container[];

        submodel?: Submodel;
        items?: ContainerItem[] | any[]; // Temporarily allow any[] for backward compatibility during gradual typing
    };
    usageInfo: {
        usedComponents: UsedComponent[] | any[]; // Temporarily allow any[] for backward compatibility
    };
}
// Base interface for common properties
interface BaseContainerItem {
    id: string;
    originalComponentId?: string;
    isReusable: boolean;
    parentId?: string;
    configuration?: any;
}

export interface WidgetItem extends BaseContainerItem {
    type: 'widget';
    widgetType: string;
    content?: string;
    config?: any;
}

export interface FieldItem extends BaseContainerItem {
    type: 'field';
    fieldType: string;
    name: string;
    dataConfiguration?: any;
}

export interface CategoryItem extends BaseContainerItem {
    type: 'category';
    fieldType?: 'category';
    name: string;
    dataConfiguration?: any;
}

export interface ConnectorItem extends BaseContainerItem {
    type: 'connector';
    fieldType?: 'connector';
    name: string;
}

// API compatibility type for nested containers in items array (doesn't extend BaseContainerItem)
export interface NestedContainerItem {
    nestedContainers: Container[];
}

// Union type for all possible container items
export type ContainerItem = WidgetItem | FieldItem | CategoryItem | ConnectorItem | NestedContainerItem;

// Type guards
export function isWidgetItem(item: ContainerItem): item is WidgetItem {
    return 'type' in item && item.type === 'widget';
}

export function isFieldItem(item: ContainerItem): item is FieldItem {
    return 'type' in item && item.type === 'field';
}

export function isCategoryItem(item: ContainerItem): item is CategoryItem {
    return 'type' in item && item.type === 'category';
}

export function isConnectorItem(item: ContainerItem): item is ConnectorItem {
    return 'type' in item && item.type === 'connector';
}

export function isNestedContainerItem(item: ContainerItem): item is NestedContainerItem {
    return 'nestedContainers' in item;
}

export function hasBaseProperties(item: ContainerItem): item is WidgetItem | FieldItem | CategoryItem | ConnectorItem {
    return !isNestedContainerItem(item);
}

interface SubmodelConfiguration {
    showTitle?: boolean;
    displayName?: string;
    hideIfNoProducts?: boolean;
    exclusionType?: 'hide' | 'disable';
}

export interface Submodel {
    id: string;
    name: string;
    isTemporary?: boolean;
    configuration?: SubmodelConfiguration;
}

export interface ContainerConfiguration {
    width: string;
    height: string;
    alignment: 'left' | 'center' | 'right';
    layout: 'vertical' | 'horizontal';
    showTitle: boolean;
    widthLocked?: boolean;
    collapsible?: boolean;
    behaveAsFilter?: boolean;
}

export interface ContainerConfig {
    id: string;
    name: string;
    width: number;
    widthUnit: '%' | 'px';
    height: 'Dynamic' | number;
    heightUnit: 'px';
    alignment: 'Left' | 'Centre' | 'Right';
    layout: 'Horizontal' | 'Vertical';
    showTitle: boolean;
    showSelectedOptions: boolean;
    widthLocked?: boolean;
    components: {
        id: string;
        type: 'widget' | 'field' | 'category';
        name: string;
        parent?: string;
    }[];
}

export interface ContainerContent {
    type: 'widget' | 'subModel';
    'widget-type'?: WidgetType;
    modelId?: string;
    fields?: Field[];
    componentId: string;
}

export interface ContainerUsageInfo {
    usedComponents: UsedComponent[];
}

// export interface UsedComponent {
//     id: string;
//     type: 'widget' | 'subModel';
//     originalComponentId: string;
//     isReusable: boolean;
// }
export interface UsedComponent {
    id: string;
    type: 'widget' | 'subModel' | 'field' | 'category' | 'connector';
    originalComponentId: string;
    isReusable: boolean;
    content?: string;
}

